import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, Calendar, Clock, Users, IndianRupee, CheckCircle, AlertCircle } from "lucide-react";
import { useLocation } from "wouter";
import { getLoginUrl } from "@/const";

export default function AmenitiesBooking() {
  const [selectedAmenity, setSelectedAmenity] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedTier, setSelectedTier] = useState<number | null>(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [, setLocation] = useLocation();

  const { user, isAuthenticated } = useAuth();

  // Fetch amenities
  const { data: amenities, isLoading: amenitiesLoading } = trpc.amenities.list.useQuery({} as any);
  
  // Fetch bookings for selected amenity
  const { data: bookings } = trpc.amenities.getBookings.useQuery(
    { amenityId: selectedAmenity || 0 },
    selectedAmenity ? {} : { enabled: false }
  ) as any;

  // Mutations
  const createBooking = trpc.amenities.createBooking.useMutation();
  const createPaymentSession = trpc.amenities.createPaymentSession.useMutation();

  const currentAmenity = amenities?.find((a) => a.id === selectedAmenity);
  const selectedTierData = currentAmenity?.pricingTiers?.find((t) => t.id === selectedTier);

  // Calculate available dates (next 90 days, excluding weekends and booked dates)
  const availableDates = useMemo(() => {
    const dates = [];
    const today = new Date();
    const bookedDates = new Set(bookings?.map((b: any) => b.bookingDate) || []);

    for (let i = 1; i <= 90; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() + i);

      // Skip Sundays (0) and Saturdays (6)
      if (date.getDay() === 0 || date.getDay() === 6) continue;

      const dateStr = date.toISOString().split("T")[0];
      if (!bookedDates.has(dateStr)) {
        dates.push(dateStr);
      }
    }

    return dates;
  }, [bookings]);

  const handleProceedToPayment = async () => {
    if (!isAuthenticated) {
      window.location.href = getLoginUrl("/amenities-booking");
      return;
    }

    if (!selectedDate || !selectedTier || !currentAmenity) {
      alert("Please select a date and pricing option");
      return;
    }

    setIsProcessing(true);
    try {
      // Step 1: Create booking
      const bookingResult = await createBooking.mutateAsync({
        amenityId: currentAmenity.id,
        pricingTierId: selectedTier,
        bookingDate: selectedDate,
        totalPrice: selectedTierData?.price || 0,
        securityDepositAmount: currentAmenity.securityDeposit || 0,
      });

      if (!bookingResult.success) {
        throw new Error("Failed to create booking");
      }

      // Step 2: Create payment session
      const paymentResult = await createPaymentSession.mutateAsync({
        bookingId: bookingResult.bookingId,
        amenityId: currentAmenity.id,
        totalPrice: selectedTierData?.price || 0,
        securityDeposit: currentAmenity.securityDeposit || 0,
        bookingDate: selectedDate,
      });

      if (paymentResult.checkoutUrl) {
        // Open Stripe checkout in new tab
        window.open(paymentResult.checkoutUrl, "_blank");
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Failed to process booking. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  if (amenitiesLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="animate-spin w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-4">
            Amenities Booking
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-300">
            Reserve our premium amenities for your events and celebrations
          </p>
        </div>

        {/* Amenities Grid */}
        {!selectedAmenity ? (
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {amenities?.filter((a) => a.isBookable).map((amenity) => (
              <Card
                key={amenity.id}
                className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => setSelectedAmenity(amenity.id)}
              >
                <div className="aspect-video bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                  {amenity.image ? (
                    <img src={amenity.image} alt={amenity.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-white text-center">
                      <div className="text-4xl mb-2">📍</div>
                      <p className="font-semibold">{amenity.name}</p>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                    {amenity.name}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 mb-4">
                    {amenity.description}
                  </p>

                  {/* Pricing */}
                  <div className="space-y-2 mb-4">
                    {amenity.pricingTiers && amenity.pricingTiers.length > 0 ? (
                      amenity.pricingTiers.map((tier) => (
                        <div key={tier.id} className="flex justify-between items-center text-sm">
                          <span className="text-slate-600 dark:text-slate-400">{tier.tierName}</span>
                          <span className="font-semibold text-slate-900 dark:text-white flex items-center gap-1">
                            <IndianRupee className="w-4 h-4" />
                            {tier.price}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="text-slate-600 dark:text-slate-400">
                        Security Deposit: ₹{amenity.securityDeposit}
                      </div>
                    )}
                  </div>

                  {/* Status Badge */}
                  <div className="flex items-center gap-2 mb-4">
                    {amenity.availability === "available" ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-green-600">Available</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-4 h-4 text-red-600" />
                        <span className="text-sm text-red-600">{amenity.availability}</span>
                      </>
                    )}
                  </div>

                  <Button className="w-full">Book Now</Button>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          /* Booking Details View */
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left: Amenity Details */}
            <div className="lg:col-span-2">
              <Card className="p-8 mb-6">
                <Button
                  variant="outline"
                  className="mb-6"
                  onClick={() => {
                    setSelectedAmenity(null);
                    setShowBookingForm(false);
                  }}
                >
                  ← Back to Amenities
                </Button>

                <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">
                  {currentAmenity?.name}
                </h2>
                <p className="text-slate-600 dark:text-slate-400 mb-6">
                  {currentAmenity?.description}
                </p>

                {/* Pricing Tiers */}
                <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-lg mb-6">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                    Pricing Options
                  </h3>
                  <div className="space-y-3">
                    {currentAmenity?.pricingTiers?.map((tier) => (
                      <div
                        key={tier.id}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedTier === tier.id
                            ? "border-blue-600 bg-blue-50 dark:bg-blue-900"
                            : "border-slate-200 dark:border-slate-700"
                        }`}
                        onClick={() => setSelectedTier(tier.id)}
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-slate-900 dark:text-white">
                            {tier.tierName}
                          </span>
                          <span className="text-lg font-bold text-blue-600">₹{tier.price}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Security Deposit */}
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4 rounded-lg mb-6">
                  <p className="text-sm text-amber-900 dark:text-amber-200">
                    <strong>Security Deposit:</strong> ₹{currentAmenity?.securityDeposit}
                  </p>
                </div>
              </Card>
            </div>

            {/* Right: Date & Booking */}
            <div>
              <Card className="p-6 sticky top-4">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Select Date
                </h3>

                {/* Date Picker */}
                <div className="mb-6">
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    min={new Date().toISOString().split("T")[0]}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                  />
                </div>

                {/* Available Dates */}
                <div className="mb-6">
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                    Available Dates (Next 90 days)
                  </p>
                  <div className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                    {availableDates.map((date) => (
                      <button
                        key={date}
                        onClick={() => setSelectedDate(date)}
                        className={`p-2 text-xs rounded-lg transition-all ${
                          selectedDate === date
                            ? "bg-blue-600 text-white"
                            : "bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-600"
                        }`}
                      >
                        {new Date(date).getDate()}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Summary */}
                {selectedDate && selectedTier && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mb-6">
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-3">
                      Booking Summary
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600 dark:text-slate-400">Date:</span>
                        <span className="font-semibold text-slate-900 dark:text-white">
                          {new Date(selectedDate).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600 dark:text-slate-400">Amenity:</span>
                        <span className="font-semibold text-slate-900 dark:text-white">
                          {currentAmenity?.name}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600 dark:text-slate-400">Amount:</span>
                        <span className="font-semibold text-blue-600">
                          ₹{selectedTierData?.price}
                        </span>
                      </div>
                      <div className="border-t border-slate-200 dark:border-slate-700 pt-2 mt-2 flex justify-between">
                        <span className="text-slate-600 dark:text-slate-400">Total (incl. deposit):</span>
                        <span className="font-bold text-slate-900 dark:text-white">
                          ₹{(selectedTierData?.price || 0) + (currentAmenity?.securityDeposit || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  className="w-full"
                  disabled={!selectedDate || !selectedTier || isProcessing}
                  onClick={handleProceedToPayment}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    "Proceed to Payment"
                  )}
                </Button>

                {!isAuthenticated && (
                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-3 text-center">
                    You will be asked to login before payment
                  </p>
                )}
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
